import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/LanguageContext";
import { Navigate, Link } from "react-router-dom";
import { getMyOrders } from "@/lib/api";

const STATUS_LABEL = {
  paid: "order.status.paid",
  preparing: "order.status.preparing",
  shipped: "order.status.shipped",
  delivered: "order.status.delivered",
  refunded: "order.status.refunded",
  cancelled: "order.status.cancelled",
};

const money = (n, cur = "EUR") =>
  new Intl.NumberFormat("en-EU", { style: "currency", currency: cur }).format(Number(n || 0));

const Account = () => {
  const { t } = useT();
  const { user, logout, loading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    getMyOrders()
      .then(setOrders)
      .catch(() => setOrders([]))
      .finally(() => setOrdersLoading(false));
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;

  return (
    <div className="pt-32 px-6 md:px-12 pb-24 max-w-3xl">
      <div className="font-badge text-[10px] tracking-[0.3em] uppercase text-[#8A7568]">{t("account.eyebrow")}</div>
      <h1 className="font-serif-display text-5xl md:text-6xl mt-3 tracking-tight text-[#241C18]">
        {t("account.welcome", { name: user.name.split(" ")[0] })}
      </h1>

      {/* Orders */}
      <section className="mt-14">
        <div className="flex items-center justify-between border-b border-[#F1DCC5] pb-3 mb-4">
          <div className="font-badge text-[11px] tracking-[0.24em] uppercase text-[#241C18]">
            {t("account.orders.title")}
          </div>
          <div className="text-xs text-[#8A7568]">{orders.length} {t("account.orders.count")}</div>
        </div>
        {ordersLoading ? (
          <div className="text-[#8A7568] text-sm">{t("account.orders.loading")}</div>
        ) : orders.length === 0 ? (
          <div className="text-[#8A7568] text-sm">{t("account.orders.empty")}</div>
        ) : (
          <div data-testid="account-orders-list" className="space-y-3">
            {orders.map((o) => (
              <Link
                key={o.id}
                to={`/order/status/${o.session_id}`}
                data-testid={`account-order-${o.order_number}`}
                className="flex items-center gap-4 p-3 bg-white border border-[#F1DCC5] rounded-2xl hover:border-[var(--gl-orange)] transition"
              >
                {o.items?.[0]?.image && (
                  <img src={o.items[0].image} alt="" className="w-14 h-16 object-cover" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-serif-display text-lg text-[#241C18] truncate">{o.order_number}</div>
                  <div className="text-xs text-[#8A7568]">
                    {new Date(o.created_at).toLocaleDateString()} · {(o.items || []).length} {t("account.orders.items")}
                  </div>
                </div>
                <div className="text-sm text-[#241C18]">{money(o.total, o.currency)}</div>
                <div className="font-badge text-[10px] tracking-[0.18em] uppercase text-[var(--gl-orange-hover)]">
                  {t(STATUS_LABEL[o.status] || "order.status.paid")}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <div className="mt-10 border-t border-[#F1DCC5] pt-6 grid md:grid-cols-2 gap-6">
        <div>
          <div className="font-badge text-[10px] tracking-[0.3em] uppercase text-[#8A7568]">{t("account.email")}</div>
          <div className="mt-1">{user.email}</div>
        </div>
        <div>
          <div className="font-badge text-[10px] tracking-[0.3em] uppercase text-[#8A7568]">{t("account.since")}</div>
          <div className="mt-1">{t("account.sinceValue")}</div>
        </div>
      </div>

      <div className="mt-10 flex gap-3">
        <Link to="/collection/all" className="btn-orange">{t("account.continue")}</Link>
        <button onClick={logout} className="text-xs underline underline-offset-2 text-[#8A7568] hover:text-[#241C18] self-center">
          {t("account.logout")}
        </button>
      </div>
    </div>
  );
};

export default Account;
